// ATW-7285 Reinstate below linting rule once timetable recent search is reinstated
/* eslint no-unused-vars: 0 */

import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import ATsearch from '@at.govt.nz/search';
import initActionCreators from '../../redux/RouteAutosuggestRedux';
import {
  getTimetableRoutes,
  getRouteLabel,
  getRouteLabelDisplay,
  selectionIsNotFoundMessage,
  SEARCH_MODE,
} from '../../services/TimetablesService';
import AliasMacronService from '../../services/AliasMacronService';

const DEBOUNCE_MS = 500;

const bem = 'tt-route-autosuggest';

const computePlaceholder = (searchMode) => {
  switch (searchMode) {
    case SEARCH_MODE.BUS: return 'e.g. 161 or New Lynn';
    case SEARCH_MODE.TRAIN: return 'e.g. South or Waitematā';
    case SEARCH_MODE.FERRY: return 'e.g. Half moon bay';
    default: return 'Enter route';
  }
};

const searchModeToLabel = (searchMode) => {
  switch (searchMode) {
    case SEARCH_MODE.BUS: return 'Bus or route number';
    case SEARCH_MODE.TRAIN: return 'Train line or destination';
    case SEARCH_MODE.FERRY: return 'Ferry route or destination';
    default: return 'Search criteria';
  }
};

class RouteAutosuggest extends React.PureComponent {
  constructor(props) {
    super(props);
    this.callDebouncedDispatchFetchSuggestions = _.debounce(this.callDispatchFetchSuggestions, DEBOUNCE_MS);

    const { location } = props;
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('route_type') && urlParams.get('name')) {
      const routeType = urlParams.get('route_type');
      const name = urlParams.get('name');
      this.initRoute(name, routeType);
    }
  }

  initRoute = async (name, routeType) => {
    const { storageKey, dispatchSelectSuggestion, dispatchSetQuery } = this.props;
    const routes = await getTimetableRoutes({ text: name, searchMode: routeType });
    if (!_.isEmpty(routes)) {
      dispatchSetQuery(name);
      dispatchSelectSuggestion(routes[0], storageKey);
    }
  };

  callDispatchFetchSuggestions = (query) => {
    const { dispatchFetchSuggestions } = this.props;
    dispatchFetchSuggestions(query);
  };

  onFetchRequested = ({ value }) => {
    const { storageKey, selection, dispatchSetQuery } = this.props;
    const { dispatchFetchRecentSearches } = this.props;
    dispatchSetQuery(value);
    // if a route is currently selected, do not fetch it again
    if (!selection || getRouteLabel(selection) !== value) {
      /*
      ATW-7285 Disabling recent search temporarily. Once issues are resolved it will be reinstated.
      dispatchFetchRecentSearches(value, storageKey);
      */
      if (!_.isEmpty(value)) {
        this.callDebouncedDispatchFetchSuggestions(value);
      }
    }
  };

  onSuggestionSelected = (event, { suggestion: selected }) => {
    if (selectionIsNotFoundMessage(selected)) {
      return;
    }
    const { storageKey, dispatchSelectSuggestion } = this.props;
    dispatchSelectSuggestion(selected, storageKey);
  };

  getSuggestionValue = (suggestion) => {
    const { query, dispatchSetQuery } = this.props;
    if (selectionIsNotFoundMessage(suggestion)) {
      return query;
    }
    const queryNew = getRouteLabel(suggestion);
    dispatchSetQuery(queryNew);
    return queryNew;
  };

  renderSuggestion = (suggestion, { isHighlighted }) => {
    const routeLabel = getRouteLabel(suggestion);

    const classNames = [`${bem}__suggestion`];
    if (routeLabel === this.props.query && isHighlighted) {
      classNames.push(`${bem}__suggestion--highlighted`);
    }

    return (
      <div className={`${bem}__suggestion-wrapper`}>
        <div className={classNames.join(' ')}>
          <span>{ getRouteLabelDisplay(suggestion) }</span>
        </div>
      </div>
    );
  };

  renderSectionTitle = section => (section.title ? <span>{ section.title }</span> : null);

  getSectionSuggestions = section => section.results || [];

  composeSuggestionsLiveRegionMessage
    = suggestions => (suggestions && suggestions.length > 0
      ? `${suggestions.length} suggestions displayed. To navigate use up and down arrow keys.`
      : '');

  render() {
    const { suggestions, fetching, searchMode } = this.props;
    const { query, dispatchClearSuggestions, dispatchResetSearch } = this.props;
    const isRecentSearchesOnly = _.isEmpty(query);

    return (
      <div className={`${bem}`}>
        <div id="search-mode-label" className={`${bem}__label`}>{searchModeToLabel(searchMode)}</div>
        <ATsearch
          multiSection={isRecentSearchesOnly}
          ariaLabelledBy="search-mode-label"
          fetching={fetching}
          suggestions={isRecentSearchesOnly ? [{ results: suggestions, title: 'Recent searches / Rapunga tata' }] : suggestions}
          suggestionsLiveRegionMessage={this.composeSuggestionsLiveRegionMessage(suggestions)}
          onCleared={dispatchResetSearch}
          onSuggestionsFetchRequested={this.onFetchRequested}
          onSuggestionsClearRequested={dispatchClearSuggestions}
          onSuggestionSelected={this.onSuggestionSelected}
          renderSectionTitle={this.renderSectionTitle}
          renderSuggestion={this.renderSuggestion}
          getSuggestionValue={this.getSuggestionValue}
          getSectionSuggestions={this.getSectionSuggestions}
          name="routeAutosuggestInput"
          placeholder={computePlaceholder(searchMode)}
          onChange={this.onInputChange}
          value={AliasMacronService.getMacronisedText(query)}
        />
      </div>
    );
  }
}

RouteAutosuggest.defaultProps = {
  selection: null,
};
RouteAutosuggest.propTypes = {
  dispatchSetQuery: PropTypes.func.isRequired,
  dispatchFetchRecentSearches: PropTypes.func.isRequired,
  dispatchFetchSuggestions: PropTypes.func.isRequired,
  dispatchSelectSuggestion: PropTypes.func.isRequired,
  dispatchClearSuggestions: PropTypes.func.isRequired,
  dispatchResetSearch: PropTypes.func.isRequired,
  searchMode: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  suggestions: PropTypes.array.isRequired,
  fetching: PropTypes.bool.isRequired,
  selection: PropTypes.object,
  storageKey: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  const searchMode = state.timetables.searchMode;
  const searchModeState = state.timetables[searchMode];
  return {
    searchMode,
    query: searchModeState.routeAutosuggest.query,
    suggestions: searchModeState.routeAutosuggest.suggestions,
    fetching: searchModeState.routeAutosuggest.fetching,
    selection: searchModeState.routeAutosuggest.selection,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps;
  const { searchMode } = stateProps;
  const actionCreators = initActionCreators(searchMode);
  return {
    ...ownProps,
    ...stateProps,
    dispatchSetQuery: query => dispatch(actionCreators.setQuery(query)),
    dispatchFetchRecentSearches: (query, baseStorageKey) => dispatch(actionCreators.fetchRecentSearches(query, baseStorageKey)),
    dispatchFetchSuggestions: query => dispatch(actionCreators.fetchSuggestions(query)),
    dispatchSelectSuggestion: (selected, baseStorageKey) => dispatch(actionCreators.selectSuggestion(selected, baseStorageKey)),
    dispatchClearSuggestions: () => dispatch(actionCreators.clearSuggestions()),
    dispatchResetSearch: () => dispatch(actionCreators.resetSearch()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(withRouter(RouteAutosuggest));
